import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { requestPasswordReset } from '../api/userApi';

// Public page — same visual language as loginForm.jsx.
// Backend always returns { ok: true } (anti-enumeration), so we show the
// same success state regardless of whether the email matched a user.
export default function ForgotPassword() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setLoading(true);
        try {
            await requestPasswordReset(email.trim());
        } catch {
            // Even on network error we show the same confirmation — the user
            // shouldn't see different states based on whether the address exists.
        } finally {
            setLoading(false);
            setSubmitted(true);
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: '#F8FAFC',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
        }}>
            <div style={{
                width: '100%',
                maxWidth: 420,
                borderRadius: 14,
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                padding: '40px 32px',
                background: 'white',
                boxSizing: 'border-box',
            }}>
                <img src="/nexwaveLogo.png" alt="Nexwave" style={{ height: 64, display: 'block', margin: '0 auto 12px' }} />
                <div style={{ textAlign: 'center', fontFamily: 'Poppins', fontWeight: 700, fontSize: 24, color: '#0F172A', marginBottom: 4 }}>
                    Forgot password?
                </div>
                <div style={{ textAlign: 'center', fontSize: 14, color: '#64748B', fontWeight: 400, fontFamily: 'Poppins', marginBottom: 32 }}>
                    We'll email you a reset link.
                </div>

                {submitted ? (
                    <div style={{
                        background: '#ECFDF5',
                        border: '1px solid #A7F3D0',
                        borderRadius: 8,
                        padding: 16,
                        color: '#065F46',
                        fontSize: 14,
                        fontFamily: 'Poppins',
                        lineHeight: 1.5,
                        marginBottom: 24,
                    }}>
                        {/* The spam line used to be the tail of a run-on sentence and nobody
                            read it. It is the single most likely reason the mail "never
                            arrived" — Yahoo and Rocketmail complete only ~70% of resets
                            against 93% for Gmail — so it gets its own line, with the sender
                            address to search for and what to do when it really is missing.
                            (user 2026-10-08: "reset page completed right can tell its in spam
                            if not in main") */}
                        <div style={{ marginBottom: 10 }}>
                            If <strong>{email}</strong> matches an account, a reset link is on its way.
                            It expires in <strong>1 hour</strong>.
                        </div>
                        <div style={{ fontWeight: 700, marginBottom: 4 }}>Don't see it?</div>
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                            <li>
                                <strong>Check your spam or junk folder.</strong> Yahoo, Rocketmail
                                and Hotmail put it there most often.
                            </li>
                            <li>
                                Search your mail for <strong>noreply@nexwaveintl.com</strong> — it may
                                have skipped the inbox entirely.
                            </li>
                            <li>
                                Still nothing after 5 minutes? The address on your account may be a
                                different one. Ask the office to check it for you.
                            </li>
                        </ul>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block', fontFamily: 'Poppins' }}>
                            Email address
                        </label>
                        <input
                            type="email"
                            placeholder="you@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            style={{
                                width: '100%',
                                padding: '10px 14px',
                                borderRadius: 8,
                                border: '1px solid #E2E8F0',
                                fontSize: 14,
                                fontFamily: 'Poppins',
                                marginBottom: 24,
                                outline: 'none',
                                boxSizing: 'border-box',
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#1A8CFF'}
                            onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                        />

                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                width: '100%',
                                padding: '12px',
                                background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)',
                                color: 'white',
                                border: 'none',
                                borderRadius: 8,
                                fontSize: 15,
                                fontWeight: 600,
                                cursor: loading ? 'default' : 'pointer',
                                fontFamily: 'Poppins',
                                opacity: loading ? 0.7 : 1,
                            }}
                        >
                            {loading ? 'Sending…' : 'Send reset link'}
                        </button>
                    </form>
                )}

                <div style={{ textAlign: 'center', marginTop: 24, fontSize: 13, fontFamily: 'Poppins' }}>
                    <Link to="/" style={{ color: '#1A8CFF', fontWeight: 600, textDecoration: 'none' }}>
                        ← Back to login
                    </Link>
                </div>
            </div>
        </div>
    );
}
